import React from 'react';
import { Client, ExpertClient } from '../../../types/client';

interface ClientCardProps {
  client: Client | ExpertClient;
  onViewDetails: (clientId: string) => void;
  onCreateBooking: (clientId: string) => void;
}

// Type guard to check if it's an ExpertClient
const isExpertClient = (client: Client | ExpertClient): client is ExpertClient => {
  return 'effective_name' in client;
};

const ClientCard: React.FC<ClientCardProps> = ({ client, onViewDetails, onCreateBooking }) => {
  const getInitials = (name: string | null, email: string | null) => {
    if (name) {
      const parts = name.trim().split(' ');
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    }
    if (email) {
      return email.substring(0, 2).toUpperCase();
    }
    return '??';
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Normalize data based on type
  const displayName = isExpertClient(client)
    ? client.effective_name
    : (client.name || client.email);
  const email = isExpertClient(client)
    ? client.effective_email
    : client.email;
  const phone = isExpertClient(client)
    ? client.effective_phone
    : client.phone;
  const isContact = isExpertClient(client)
    ? !client.linked
    : client.is_contact;
  const bookingsCount = isExpertClient(client)
    ? client.stats.total_bookings
    : client.bookings_count;
  const totalSpent = isExpertClient(client)
    ? '$0.00' // TODO: Add total_spent to ExpertClient stats
    : client.total_spent_formatted;
  const lastBooking = isExpertClient(client)
    ? client.last_interaction_at
    : client.last_booking_at;

  const initials = getInitials(displayName, email);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-4">
        {/* Avatar */}
        <div className="flex-shrink-0">
          <div className="w-12 h-12 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center font-semibold text-lg">
            {initials}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-lg font-semibold text-gray-900 truncate">
              {displayName}
            </h3>
            <span
              className={`px-2 py-1 text-xs font-medium rounded-full ${
                isContact
                  ? 'bg-gray-100 text-gray-700'
                  : 'bg-blue-100 text-blue-700'
              }`}
            >
              {isContact ? 'Contact' : 'Client'}
            </span>
          </div>

          {/* Contact Info */}
          <div className="space-y-1 mb-3">
            {email && <p className="text-sm text-gray-600 truncate">{email}</p>}
            {phone && <p className="text-sm text-gray-600">{phone}</p>}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-gray-500">Bookings</p>
              <p className="text-sm font-semibold text-gray-900">{bookingsCount}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Total Spent</p>
              <p className="text-sm font-semibold text-gray-900">{totalSpent}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Last Booking</p>
              <p className="text-sm font-semibold text-gray-900">{formatDate(lastBooking)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-4 pt-4 border-t border-gray-200 flex gap-2">
        <button
          onClick={() => onViewDetails(client.id)}
          className="flex-1 px-4 py-2 text-sm font-medium text-purple-600 bg-purple-50 rounded-md hover:bg-purple-100 transition-colors"
        >
          View Details
        </button>
        <button
          onClick={() => onCreateBooking(client.id)}
          className="flex-1 px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 transition-colors"
        >
          Create Booking
        </button>
      </div>
    </div>
  );
};

export default ClientCard;
